"use client";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import posthog from "posthog-js";

const COOKIE_CONSENT_KEY = "caroffshore-cookie-consent";

// Add proper window type declaration for doNotTrack
declare global {
  interface Window {
    doNotTrack?: string;
  }
  interface Navigator {
    msDoNotTrack: string | null;
  }
}

// Cookie consent preferences type
export type CookiePreferences = {
  essential: boolean; // Always true, can't be toggled
  marketing: boolean;
  personalization: boolean;
  analytics: boolean;
};

// Default preferences - essential is always true
const defaultPreferences: CookiePreferences = {
  essential: true,
  marketing: false,
  personalization: false,
  analytics: true,
};

// Context interface
interface CookieConsentContextType {
  preferences: CookiePreferences;
  isConsentGiven: boolean;
  showBanner: boolean;
  showModal: boolean;
  setShowBanner: (show: boolean) => void;
  setShowModal: (show: boolean) => void;
  updatePreferences: (newPreferences: Partial<CookiePreferences>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: () => void;
}

// Create the context
const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

// Hook for using the cookie consent
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider"
    );
  }
  return context;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

// Check if browser has Do Not Track enabled
const hasDoNotTrackEnabled = () => {
  if (typeof window === "undefined") return false;

  // Safely check DNT settings across browsers
  const dntEnabled =
    (window as any).doNotTrack === "1" ||
    (navigator as any).doNotTrack === "1" ||
    (navigator as any).doNotTrack === "yes" ||
    (navigator as any).msDoNotTrack === "1";

  return dntEnabled;
};

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
}) => {
  // State for preferences
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);
  const [isConsentGiven, setIsConsentGiven] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const initializeConsent = () => {
      try {
        const storedPreferences = localStorage.getItem(COOKIE_CONSENT_KEY);

        if (storedPreferences) {
          // User has already given consent
          const parsedPreferences = JSON.parse(storedPreferences);
          setPreferences({
            ...defaultPreferences,
            ...parsedPreferences,
            essential: true, // Essential is always true
          });
          setIsConsentGiven(true);
          setShowBanner(false);

          // Initialize PostHog if analytics consent is already given
          if (parsedPreferences.analytics) {
            posthog.opt_in_capturing();
            posthog.capture("$pageview");
          }
        } else {
          // No consent given yet, respect DNT if enabled
          const dntEnabled = hasDoNotTrackEnabled();

          if (dntEnabled) {
            // If DNT is enabled, set all optional cookies to false
            setPreferences(defaultPreferences);
            setIsConsentGiven(true); // Consider this as explicit consent
            saveToStorage(defaultPreferences);
            setShowBanner(false);
          } else {
            // Show banner for first-time visitors
            setShowBanner(true);
            setIsConsentGiven(false);
          }
        }
      } catch (error) {
        console.error("Error loading cookie preferences:", error);
        setShowBanner(true);
      } finally {
        setIsInitialized(true);
      }
    };

    if (typeof window !== "undefined") {
      initializeConsent();
    }
  }, []);

  // Save preferences to localStorage
  const saveToStorage = (prefs: CookiePreferences) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));

      // Fallback: Also store in a cookie for browsers with localStorage limitations
      // document.cookie = `${COOKIE_CONSENT_KEY}=${JSON.stringify(prefs)};path=/;max-age=${60 * 60 * 24 * 365}`; // 1 year

      // Dispatch event to notify other components of consent change
      const event = new CustomEvent(COOKIE_CONSENT_KEY, { detail: prefs });
      window.dispatchEvent(event);
    } catch (error) {
      console.error("Error saving cookie preferences:", error);
    }
  };

  // Update specific preferences
  const updatePreferences = (newPreferences: Partial<CookiePreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      ...newPreferences,
      essential: true, // Essential is always true
    }));
  };

  // Accept all cookies
  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      marketing: true,
      personalization: true,
      analytics: true,
    };
    setPreferences(allAccepted);
    setIsConsentGiven(true);
    setShowBanner(false);
    setShowModal(false);
    saveToStorage(allAccepted);

    // Trigger any necessary callbacks for enabled services
    triggerServiceCallbacks(allAccepted);
  };

  // Reject all optional cookies
  const rejectAll = () => {
    setPreferences(defaultPreferences);
    setIsConsentGiven(true);
    setShowBanner(false);
    setShowModal(false);
    saveToStorage(defaultPreferences);

    // Trigger any necessary callbacks for disabled services
    triggerServiceCallbacks(defaultPreferences);
  };

  // Save current preferences
  const savePreferences = () => {
    setIsConsentGiven(true);
    setShowBanner(false);
    setShowModal(false);
    saveToStorage(preferences);

    // Trigger any necessary callbacks based on current preferences
    triggerServiceCallbacks(preferences);
  };

  // Trigger service callbacks based on preferences
  const triggerServiceCallbacks = (prefs: CookiePreferences) => {
    // Analytics services
    if (prefs.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }

    // Marketing services
    if (prefs.marketing) {
      enableMarketing();
    } else {
      disableMarketing();
    }

    // Personalization services
    if (prefs.personalization) {
      enablePersonalization();
    } else {
      disablePersonalization();
    }
  };

  // Service-specific functions that would be implemented based on your analytics/marketing tools
  const enableAnalytics = () => {
    console.log("Analytics enabled");
    posthog.opt_in_capturing();

    if (typeof window !== "undefined" && "gtag" in window) {
      // @ts-ignore: gtag might not be in Window interface
      window.gtag("consent", "update", {
        analytics_storage: "granted",
      });
    }
  };

  const disableAnalytics = () => {
    // Implement based on your analytics service
    // For example: window.gtag('consent', 'update', { analytics_storage: 'denied' });
    console.log("Analytics disabled");
    posthog.opt_out_capturing();

    if (typeof window !== "undefined" && "gtag" in window) {
      // @ts-ignore: gtag might not be in Window interface
      window.gtag("consent", "update", {
        analytics_storage: "denied",
      });
    }
  };

  const enableMarketing = () => {
    // Implement based on your marketing services
    console.log("Marketing enabled");
  };

  const disableMarketing = () => {
    // Implement based on your marketing services
    console.log("Marketing disabled");
  };

  const enablePersonalization = () => {
    // Implement based on your personalization services
    console.log("Personalization enabled");
  };

  const disablePersonalization = () => {
    // Implement based on your personalization services
    console.log("Personalization disabled");
  };

  // Only render children after initialization to avoid flashing
  if (!isInitialized && typeof window !== "undefined") {
    return null;
  }

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        isConsentGiven,
        showBanner,
        showModal,
        setShowBanner,
        setShowModal,
        updatePreferences,
        acceptAll,
        rejectAll,
        savePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentProvider;
